import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Calendar, AlertCircle, Clock, CheckCircle } from 'lucide-react';
import { useLocale } from '../context/LocaleContext';

const TodaysFocusDashboard = ({ tasks, onTaskClick, onCompleteTask }) => {
  const { t, locale } = useLocale();
  
  // Get today's local date string for comparison
  const now = new Date();
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  
  const categorizedTasks = useMemo(() => {
    const overdue = [];
    const dueToday = [];
    const upcoming = [];
    
    tasks.filter(t => t.status === 'pending').forEach(task => {
      if (!task.dueDate) {
        return; // Skip tasks without due date
      }
      
      try {
        // Extract date string (YYYY-MM-DD) to avoid timezone issues
        const dueDateStr = typeof task.dueDate === 'string' ? task.dueDate.split('T')[0] : task.dueDate;
        
        // Parse as local dates for accurate comparison
        const [todayYear, todayMonth, todayDay] = todayStr.split('-').map(Number);
        const [dueYear, dueMonth, dueDay] = dueDateStr.split('-').map(Number);
        
        const todayDate = new Date(todayYear, todayMonth - 1, todayDay);
        const dueDate = new Date(dueYear, dueMonth - 1, dueDay);
        
        const diffTime = dueDate - todayDate;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        if (diffDays < 0) {
          overdue.push({ ...task, daysOverdue: Math.abs(diffDays) });
        } else if (diffDays === 0) {
          dueToday.push(task);
        } else if (diffDays <= 3) {
          upcoming.push({ ...task, daysUntil: diffDays });
        }
      } catch (e) {
        // Invalid date, skip
      }
    });
    
    return { overdue, dueToday, upcoming };
  }, [tasks, todayStr]);

  const formatDate = (dateStr) => {
    try {
      // Parse date string as local date to avoid timezone issues
      const str = typeof dateStr === 'string' ? dateStr.split('T')[0] : dateStr;
      const [year, month, day] = str.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString(locale?.locale || 'en-US', { month: 'short', day: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const getTotalCount = () => {
    return categorizedTasks.overdue.length + 
           categorizedTasks.dueToday.length + 
           categorizedTasks.upcoming.length;
  };

  if (getTotalCount() === 0) {
    return (
      <Card className="border-2 border-green-200 bg-gradient-to-br from-green-50 to-emerald-50">
        <CardContent className="pt-6">
          <div className="text-center py-8">
            <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('work.allCaughtUp')} 🎉</h3>
            <p className="text-sm text-gray-600">{t('work.noUrgentTasks')}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span className="flex items-center text-blue-900">
            <Calendar className="h-5 w-5 mr-2" />
            {t('work.todaysFocus')} - {now.toLocaleDateString(locale?.locale || 'en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </span>
          <Badge variant="secondary" className="bg-blue-100 text-blue-800">
            {getTotalCount()} tasks
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Overdue Tasks */}
        {categorizedTasks.overdue.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 mb-3">
              <AlertCircle className="h-5 w-5 text-red-600" />
              <h3 className="font-semibold text-red-900">
                ⚠️ {categorizedTasks.overdue.length} {t('work.overdue')}
              </h3>
            </div>
            {categorizedTasks.overdue.map((task) => (
              <div
                key={task.id}
                className="bg-red-50 border-2 border-red-300 rounded-lg p-3 hover:shadow-md transition-all cursor-pointer"
                onClick={() => onTaskClick && onTaskClick(task)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{task.title}</p>
                    <p className="text-xs text-red-700 mt-1">
                      {task.daysOverdue} {task.daysOverdue > 1 ? t('common.days') : t('common.day')} {t('work.overdue').toLowerCase()} • {t('life.due')}: {formatDate(task.dueDate)}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onCompleteTask && onCompleteTask(task);
                    }}
                    className="ml-3 bg-green-600 hover:bg-green-700"
                  >
                    <CheckCircle className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Due Today */}
        {categorizedTasks.dueToday.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 mb-3">
              <Clock className="h-5 w-5 text-orange-600" />
              <h3 className="font-semibold text-orange-900">
                🎯 {categorizedTasks.dueToday.length} {t('work.dueToday')}
              </h3>
            </div>
            {categorizedTasks.dueToday.map((task) => (
              <div
                key={task.id}
                className="bg-orange-50 border-2 border-orange-300 rounded-lg p-3 hover:shadow-md transition-all cursor-pointer"
                onClick={() => onTaskClick && onTaskClick(task)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{task.title}</p>
                    {task.estimatedTime && (
                      <p className="text-xs text-orange-700 mt-1">
                        ⏱️ {t('work.estimatedTime')}: {task.estimatedTime}
                      </p>
                    )}
                  </div>
                  <Button
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onCompleteTask && onCompleteTask(task);
                    }}
                    className="ml-3 bg-green-600 hover:bg-green-700"
                  >
                    <CheckCircle className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Coming Up (Next 3 Days) */}
        {categorizedTasks.upcoming.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 mb-3">
              <Calendar className="h-5 w-5 text-blue-600" />
              <h3 className="font-semibold text-blue-900">
                ⏰ {categorizedTasks.upcoming.length} {t('work.comingUp') || 'Coming Up'} ({t('work.next3Days') || 'Next 3 Days'})
              </h3>
            </div>
            {categorizedTasks.upcoming.slice(0, 3).map((task) => (
              <div
                key={task.id}
                className="bg-blue-50 border border-blue-200 rounded-lg p-3 hover:shadow-md transition-all cursor-pointer"
                onClick={() => onTaskClick && onTaskClick(task)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{task.title}</p>
                    <p className="text-xs text-blue-700 mt-1">
                      {t('work.dueIn') || 'Due in'} {task.daysUntil} {task.daysUntil > 1 ? (t('common.days') || 'days') : (t('common.day') || 'day')} • {formatDate(task.dueDate)}
                    </p>
                  </div>
                  <Badge variant="outline" className="ml-3 text-xs">
                    {t(`work.${task.priority}`) || task.priority}
                  </Badge>
                </div>
              </div>
            ))}
            {categorizedTasks.upcoming.length > 3 && (
              <p className="text-xs text-gray-500 text-center py-1">
                +{categorizedTasks.upcoming.length - 3} {t('work.moreUpcomingTasks') || 'more upcoming tasks'}
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TodaysFocusDashboard;
